import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Rocket, Loader2, Layers } from "lucide-react";
import { publishingApi } from "@/lib/publishingApi";

const STATE_COLOR = {
  Draft: "text-slate-400", Building: "text-blue-400", Uploaded: "text-cyan-400",
  "Waiting for Review": "text-amber-400", "In Review": "text-amber-300",
  Approved: "text-emerald-400", Rejected: "text-red-400", Released: "text-[#00F0FF]",
};

export default function PublishHome() {
  const [data, setData] = useState(null);

  useEffect(() => { publishingApi.dashboard().then(setData).catch(() => {}); }, []);

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 left-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[140px] rounded-full" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div>
          <span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
        </Link>
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2">
          <ArrowLeft size={14} /> Builder
        </Link>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="mb-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]"><Rocket size={14} /> Publishing Dashboard</div>
        <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">Your app submissions</h1>

        {!data && <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>}

        {data && data.total === 0 && (
          <div className="mt-12 rounded-3xl border border-white/10 bg-[#0a0a0a] p-12 text-center" data-testid="pub-empty">
            <Layers size={38} className="text-slate-700 mx-auto mb-4" />
            <p className="text-slate-400">No publishing projects yet.</p>
            <Link to="/#chat" className="mt-6 inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-2xl font-semibold hover:bg-[#00F0FF] transition-colors duration-300">Build an app first →</Link>
          </div>
        )}

        {data && data.total > 0 && (
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-testid="pub-dashboard">
            {data.states.map((state) => (
              <div key={state} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 min-h-[120px]">
                <p className={`font-mono text-[11px] uppercase tracking-widest mb-3 ${STATE_COLOR[state] || "text-slate-400"}`}>{state} · {data.columns[state]?.length || 0}</p>
                <div className="space-y-2">
                  {(data.columns[state] || []).map((p) => (
                    <Link key={p.id} to={`/publish/${p.id}`} className="block rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 hover:border-[#0055FF] transition-colors duration-300" data-testid={`pub-card-${p.id}`}>
                      <p className="text-white text-sm font-medium truncate">{p.name}</p>
                      <p className="text-slate-500 text-xs">{p.platform}</p>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
